import { useLocale } from '../../application/i18n/LocaleProvider';
import { headlineSpecs } from '../../domain/cars/carProperties';
import CarSpecPlateCell from './CarSpecPlateCell';

/**
 * The four figures, as the plate §12 draws across the foot of a card.
 *
 * These are the specs a fourteen-year-old could recite — top speed, power,
 * 0–100, weight — and the selection rule is exactly that. Which four, and in
 * which order, is `carSchema.HEADLINE_SPECS`; this component never chooses.
 *
 * ALWAYS FOUR COLUMNS, even where the catalogue is missing a figure: a car that
 * dropped a column would be drawn differently from every other card in the deck,
 * and the deck is a stack of identical objects with different cars on them. The
 * domain answers an em dash for a number nobody has, which is a fact rather than
 * a hole.
 *
 * The NUMBER is the point, so the number is the type: a big tabular figure with
 * its unit small underneath and the label smaller still. A label above a figure
 * would be a spec sheet; the figure first is a dashboard, which is what this is.
 *
 * Drawn as a `<dl>`, because that is what it is — four terms and their values —
 * and the reading order in the markup is term-then-value even though the pixels
 * put the value on top. The short label is what a screen reader gets; four
 * columns across half a phone leave each one about 40px, which is why the short
 * vocabulary exists at all.
 */
export default function CarSpecPlate({ car }) {
    // The grouping of a figure is a fact about a language, not copy: 1,100 in
    // English is 1.100 in German. It is the domain's to apply and this
    // component's to pass, which is the whole reason the parameter is there.
    const { languageCode } = useLocale();

    return (
        <dl className="spec-plate">
            {headlineSpecs(car, { languageCode }).map((spec) => (
                /* KEYED BY THE FIGURE, NOT BY THE CAR. The cell is meant to
                   survive a deal — that is what lets it roll from the last
                   car's number to this one's rather than mounting fresh at the
                   new value — so the key has to be the thing that is stable
                   across cars, which is which of the four instruments this is. */
                <CarSpecPlateCell key={spec.key} spec={spec} languageCode={languageCode} />
            ))}
        </dl>
    );
}
